import { i18n } from '@goodboydigital/astro';
import SoundBoy from '@goodboydigital/soundboy';
import gsap from 'gsap';
import { Container, Sprite, Text, Texture, Ticker } from 'pixi.js';
import { SignalConnection } from 'typed-signals';

import { App, Shared } from '../../App';
import { AnalyticsState } from '../../plugins/AnalyticsPlugin';
import { CloseButton } from '../buttons/CloseButton';
import { RectangleButton } from '../buttons/RectangleButton';
import { Card, CardOpts } from '../Card';
import LocalisedText from '../LocalisedText';
import { Panel } from '../Panel';

const defaultOpts = {
    title: '',
    titleParams: undefined as any,
    cardOpts: undefined as Partial<CardOpts> | undefined,
    callback: undefined as (() => void) | undefined
};

export type PrizePopupOptions = typeof defaultOpts;

export class PrizePopup
{
    public app: App;
    public view: Container;
    public content: Container;
    public panel: Panel;
    public shade: Sprite;
    public title: LocalisedText;
    public card: Card;
    public closeButton: CloseButton;
    public okButton: RectangleButton;
    public presenting = false;
    public options: PrizePopupOptions;

    private resizeConnection?: SignalConnection;

    constructor(app: App, opts: Partial<PrizePopupOptions> = {})
    {
        this.app = app;
        this.view = new Container();
        this.options = { ...defaultOpts, ...opts };

        this.shade = new Sprite(Texture.WHITE);
        this.shade.tint = 0x000000;
        this.shade.interactive = true;
        this.shade.anchor.set(0.5);
        this.shade.alpha = 0.9;
        this.view.addChild(this.shade);

        this.content = new Container();
        this.view.addChild(this.content);

        this.panel = new Panel({ w: 800, h: 500 });
        this.content.addChild(this.panel.view);
        this.panel.turnOnShining();

        this.card = new Card({
            image: 'diamond-large',
        });
        this.content.addChild(this.card.view);
        this.card.view.rotation = 0.2;

        const shine = Sprite.from(Shared.assets.getTexture('currency-shine'));

        shine.anchor.set(0.5);
        shine.scale.set(0.9);
        shine.x = 45;
        shine.y = -50;
        this.card.view.addChild(shine);

        this.title = new LocalisedText('', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 60,
            maxWidth: 600
        });
        this.title.anchor.set(0.5);
        this.title.y = (-this.panel.bg.height / 2) + 50;
        this.content.addChild(this.title);

        this.okButton = new RectangleButton({
            text: 'prizePopup_ok',
            fontSize: 50,
            bg: 'magenta',
            w: 300,
            h: 100,
            startHidden: true,
        });
        this.view.addChild(this.okButton.view);
        this.okButton.onPress.connect(() => this.dismiss());

        this.closeButton = new CloseButton({
            startHidden: true,
        });
        this.view.addChild(this.closeButton.view);
        this.closeButton.onPress.connect(() => this.dismiss());

        this.resize();
    }

    public async present(opts?: Partial<PrizePopupOptions>): Promise<void>
    {
        if (this.presenting) return;
        this.presenting = true;
        this.app.stage.stage.addChild(this.view);
        
        this.resizeConnection = this.app.resize.onResize.connect(() => this.resize());
        this.resize();
        
        if (opts?.title) this.title.setText(opts.title, opts.titleParams);
        if (opts?.cardOpts) this.card.refresh(opts.cardOpts);

        this.killTweens();

        this.content.scale.set(0.5);
        this.content.alpha = 1;
        this.okButton.view.scale.set(0);
        this.closeButton.view.scale.set(0);
        this.shade.alpha = 0.00001;
        
        SoundBoy.muted = this.app.user.data.settings.mute;

        Shared.game.soundSystem.playSfx('reward-prize');
        
        this.app.analytics.trackState(AnalyticsState.RACE_REWARDS, { content_type: "race rewards" });

        Ticker.system.add(this.update, this);
        gsap.to(this.shade, { alpha: 0.75, duration: 0.2 });
        await gsap.to(this.content.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out' });

        if (opts?.callback) opts?.callback();

        await this.okButton.show();

        await this.closeButton.show();
    }

    public async dismiss(): Promise<void>
    {
        if (!this.presenting) return;

        this.presenting = false;
        
        this.killTweens();
        if (this.resizeConnection)
        {
            this.resizeConnection.disconnect();
            this.resizeConnection = undefined;
        }
        
        SoundBoy.muted = this.app.user.data.settings.mute;

        this.okButton.hide();
        this.closeButton.hide();
        gsap.to(this.content.scale, { x: 1.5, y: 1.5, duration: 0.3, ease: 'back.in' });
        gsap.to(this.content, { alpha: 0, duration: 0.3, ease: 'sine.in' });
        await gsap.to(this.shade, { alpha: 0, duration: 0.2, delay: 0.1 });

        if (this.view.parent) this.view.parent.removeChild(this.view);
        Ticker.system.remove(this.update, this);

        if (Shared.game.playing) Shared.game.resume();
    }

    public resize(): void
    {
        this.view.x = this.app.resize.width / 2;
        this.view.y = this.app.resize.height / 2;
        this.shade.width = this.app.resize.width;
        this.shade.height = this.app.resize.height;

        this.okButton.view.x = 0;
        this.okButton.view.y = this.panel.bg.height / 2;

        this.closeButton.view.x = (this.panel.bg.width / 2) - 20;
        this.closeButton.view.y = (-this.panel.bg.height / 2) + 20;
    }

    private killTweens(): void
    {
        gsap.killTweensOf(this.shade);
        gsap.killTweensOf(this.okButton.view);
        gsap.killTweensOf(this.closeButton.view);
        gsap.killTweensOf(this.content);
        gsap.killTweensOf(this.content.scale);
    }

    private update(): void
    {
        this.card.view.rotation = (Math.sin(Ticker.system.lastTime * 0.005) * 0.1) + 0.2;
    }
}
